import { SKILL_CATALOG, type SkillInvocation, type SkillMode } from "~/api/conversationTypes";

/**
 * Skill invocations, parsed out of a message body (W8 §A.1 / S7).
 *
 * A skill is not a wire field: the model EMITS a fenced block and the client finds it. That is
 * the whole protocol —
 *
 * ```skill:write_document mode=append
 * ## Clause 4
 * Softer wording.
 * ```
 *
 * which means three things worth stating plainly. The parser must survive a model that
 * forgets the closing fence (nothing is rendered, rather than the rest of the message being
 * swallowed). Its invocation ids must be stable across re-renders of the same finalised
 * message, so a dismissal sticks — hence `${messageId}:${index}`. And an id that is not in
 * `SKILL_CATALOG` is not an error: it renders as "this skill isn't supported here", because a
 * server that learns a new skill before the client does must not produce a blank card.
 */

/** The fence. Non-greedy body, so two blocks in one message stay two blocks. */
const BLOCK = /```skill:([a-zA-Z0-9_-]+)([^\n]*)\n([\s\S]*?)```/g;

/** `mode=append`, `title="Two words"`, `title='Two words'` — all three forms kms emitted. */
const PARAM = /(\w+)=(?:"([^"]*)"|'([^']*)'|(\S+))/g;

export function parseSkillParams(source: string): Record<string, string> {
  const params: Record<string, string> = {};
  PARAM.lastIndex = 0;
  let match = PARAM.exec(source);
  while (match) {
    const [, key, quoted, singleQuoted, bare] = match;
    if (key) {
      params[key] = quoted ?? singleQuoted ?? bare ?? "";
    }
    match = PARAM.exec(source);
  }
  return params;
}

export function parseSkills(messageId: string, content: string): SkillInvocation[] {
  const found: SkillInvocation[] = [];
  BLOCK.lastIndex = 0;
  let match = BLOCK.exec(content);
  let index = 0;
  while (match) {
    const [, skillId = "", rawParams = "", body = ""] = match;
    found.push({
      id: `${messageId}:${index}`,
      skillId,
      params: parseSkillParams(rawParams),
      body: body.replace(/\n+$/, ""),
      descriptor: SKILL_CATALOG[skillId] ?? null,
    });
    index += 1;
    match = BLOCK.exec(content);
  }
  return found;
}

/** The message with its skill fences removed — what the reader should actually read. */
export function stripSkills(content: string): string {
  return content
    .replace(BLOCK, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const MODES: SkillMode[] = ["append", "insert", "replace_selection", "replace_document"];

/**
 * The mode a `document-write` card will use: the model's `mode=` when it named a real one,
 * otherwise the catalogue's default, otherwise append. A model that invents a mode gets the
 * safest behaviour rather than a card that cannot act.
 */
export function skillMode(invocation: SkillInvocation): SkillMode {
  const requested = invocation.params.mode as SkillMode | undefined;
  if (requested && MODES.includes(requested)) {
    return requested;
  }
  return invocation.descriptor?.defaultMode ?? "append";
}

/**
 * The title a `document-create` / `document-rename` card will use: `params.title`, else the
 * first markdown heading line stripped of its `#`, else `Untitled`. kms's rule, kept.
 */
export function skillTitle(invocation: SkillInvocation, untitled: string): string {
  const explicit = invocation.params.title?.trim();
  if (explicit) {
    return explicit;
  }
  const firstLine = invocation.body.split("\n").find((line) => line.trim().length > 0) ?? "";
  const stripped = firstLine.replace(/^#{1,6}\s*/, "").trim();
  return stripped || untitled;
}

/** What `call_mcp_tool` puts in its body: a JSON object naming the tool and its inputs. */
export interface McpCall {
  tool: string;
  serverId: string | null;
  args: Record<string, unknown>;
  extra: Record<string, unknown>;
  raw: string;
}

/**
 * Parses the MCP block. A body that is not JSON is not a failure — the trace still renders,
 * with the raw text, because "the assistant called a tool and here is what it sent" is useful
 * even when the payload is malformed.
 */
export function parseMcpCall(body: string): McpCall {
  const raw = body.trim();
  let parsed: Record<string, unknown> = {};
  try {
    const value: unknown = JSON.parse(raw);
    if (value && typeof value === "object" && !Array.isArray(value)) {
      parsed = value as Record<string, unknown>;
    }
  } catch {
    // Malformed payloads still get a trace; see the note above.
  }
  const { tool, name, serverId, arguments: args, ...extra } = parsed;
  const toolName = typeof tool === "string" ? tool : typeof name === "string" ? name : "";
  return {
    tool: toolName,
    serverId: typeof serverId === "string" ? serverId : null,
    args:
      args && typeof args === "object" && !Array.isArray(args)
        ? (args as Record<string, unknown>)
        : {},
    extra,
    raw,
  };
}
